import { DEFAULT_LOCALE } from "@/i18n/routing";
import api from "@/lib/api";
import { QUERY_STRING_OPTIONS } from "@/query-rest/common";
import {
  JapanArea,
  JapanHotel,
  JapanHotelImage,
  JapanHotelsQuery,
} from "@/types/generated";
import { del, get, set } from "idb-keyval";
import qs from "qs";
import { StateStorage } from "zustand/middleware";
import { createAppStore } from "../index";

import { fetchCMS } from "@/graphql/client/client";

import { GET_HOTEL_ALL_LOCALE } from "@/queries/globalQueries";
import { CONTENT_STATUS } from "@/constants";
import { getJapanAreasQueryConfig } from "@/query-rest/japanHotelsRest/japanHotelsRest";
import { getPublicEnv } from '@/utils/env';

interface IJapanAreaStore {
  japanAreas: JapanArea[];
  loading: boolean;
  error: string | null;
  locale: string;
  fetchAreas: (
    locale: string,
    isDraftMode?: boolean,
    forceDefaultLocale?: boolean,
    targetLocale?: string,
  ) => Promise<void>;
  setAreas: (japanAreas: JapanArea[]) => void;
  resetArea?: () => void;
  allLocaleHotels: JapanHotelsQuery | null;
  loadingAllLocaleHotels: boolean;
  fetchAllLocaleHotels: (
    isDraftMode?: boolean,
    forceDefaultLocale?: boolean,
    targetLocale?: string,
  ) => Promise<void>;
  imageFetched: boolean;
}

// IndexedDB storage with TTL
export const createExpiringIndexedDBStorage = (
  ttlMs: number,
): StateStorage => ({
  getItem: async (name) => {
    const item = await get(name);
    if (!item) return null;

    try {
      const { value, timestamp } = item;
      if (Date.now() - timestamp > ttlMs) {
        await del(name);
        return null;
      }
      return value;
    } catch {
      return null;
    }
  },
  setItem: async (name, value) => {
    await set(name, { value, timestamp: Date.now() });
  },
  removeItem: async (name) => {
    await del(name);
  },
});

// In-flight fetchAreas requests keyed by locale + draft mode
const inflightFetchAreas = new Map<string, Promise<void>>();

// Zustand store for Japan areas and hotels
const useJapanAreasStore = createAppStore<IJapanAreaStore>(
  (setState) => ({
    japanAreas: [],
    loading: false,
    error: null,
    locale: "",
    allLocaleHotels: null,
    loadingAllLocaleHotels: false,
    imageFetched: false,
    fetchAreas: (locale: string, isDraftMode = false, _forceDefaultLocale, targetLocale) => {
      const resolvedLocale = targetLocale || locale || DEFAULT_LOCALE;
      // Share one request between concurrent callers (GlobalData, search forms, booking sections)
      const inflightKey = `${resolvedLocale}|${isDraftMode}`;
      const existing = inflightFetchAreas.get(inflightKey);
      if (existing) return existing;
      const request = (async () => {
      const key = "japan-area-stores";
      const stored = await import("idb-keyval").then(({ get }) => get(key));
      const stateData = stored?.value?.state;
      const nofetch =
        !!stateData?.japanAreas?.length &&
        !!stateData?.allLocaleHotels &&
        stateData?.imageFetched &&
        stateData?.locale === resolvedLocale;
      if (nofetch) {
        setState((state) => {
          state.japanAreas = stateData.japanAreas;
          state.allLocaleHotels = stateData.allLocaleHotels;
          state.locale = resolvedLocale;
          state.loading = false;
          state.imageFetched = true;
        });
        return;
      }
      setState((state) => {
        state.loading = true;
        state.error = null;
        state.japanAreas = [];
        state.locale = resolvedLocale;
        state.imageFetched = false;
      });
      try {
        // Build query for Japan areas
        const query = qs.stringify(
          getJapanAreasQueryConfig(
            resolvedLocale,
            isDraftMode ? CONTENT_STATUS.DRAFT : CONTENT_STATUS.PUBLISHED,
          ),
          QUERY_STRING_OPTIONS,
        );

        const url = `${getPublicEnv('NEXT_PUBLIC_STRAPI_API_URL')}/japan-areas?${query}`;

        // Fetch Japan areas and all-locale hotels in parallel
        let response;
        try {
          response = await Promise.all([
            api.get(url),
            fetchCMS<JapanHotelsQuery>(GET_HOTEL_ALL_LOCALE, {
              status: isDraftMode
                ? CONTENT_STATUS.DRAFT
                : CONTENT_STATUS.PUBLISHED,
            }, resolvedLocale),
          ]);
        } catch (error) {
          console.error("Error fetching Japan areas:", error);
          throw error;
        }
        const [data, allAreas] = response || [];

        // Collect hotel image IDs for batch fetching
        const hotelImageIds = (data.data as { data: JapanArea[] }).data
          .flatMap((arr) => arr.hotels)
          .flatMap((h) => h?.hotel_images)
          .map((id) => id?.documentId)
          .filter((id): id is string => id !== undefined);

        const API_URL = getPublicEnv('NEXT_PUBLIC_STRAPI_API_URL');

        // Fetch hotel images in batch
        const hotelImagesQuery = api.post(
          `${API_URL}/mystays/hotel-image/get-japan-hotel-images`,
          {
            locale: resolvedLocale,
            filters: {
              documentId: {
                $in: hotelImageIds,
              },
              publishedAt: { $notNull: true },
            },
            status: isDraftMode
              ? CONTENT_STATUS.DRAFT
              : CONTENT_STATUS.PUBLISHED,
          },
        );

        let hotelImages;
        try {
          hotelImages = await hotelImagesQuery;
          setState((state) => {
            state.imageFetched = true;
          });
        } catch (error) {
          console.error("Error fetching data:", error);
          hotelImages = { data: [] };
        }

        // Map hotel images to respective hotels
        const areasData = (data?.data as { data: JapanArea[] }).data;

        // Loop through each area
        for (const area of areasData) {
          const hotels = (area?.hotels || []) as JapanHotel[];

          // Loop through each hotel in the area
          for (const hotel of hotels) {
            const matchedImage = (
              (hotelImages?.data as JapanHotelImage[]) || []
            ).find((hi) => hi?.documentId === hotel?.hotel_images?.documentId);

            hotel.hotel_images = matchedImage as JapanHotelImage;
          }
        }

        // Sort by area from north to south
        const areas = areasData.toSorted(
          (a, b) =>
            Number.parseInt(a.state_code as string) -
            Number.parseInt(b.state_code as string),
        );

        setState((state) => {
          state.japanAreas = areas;
          state.allLocaleHotels = allAreas;
          state.loading = false;
          state.loadingAllLocaleHotels = false;
        });
      } catch (err) {
        setState((state) => {
          state.loading = false;
          state.loadingAllLocaleHotels = false;
          state.error =
            err instanceof Error ? err.message : "Failed to fetch areas";
          state.imageFetched = false;
        });
      }
    })().finally(() => {
        inflightFetchAreas.delete(inflightKey);
      });
      inflightFetchAreas.set(inflightKey, request);
      return request;
    },
    setAreas: (newJapanAreas: JapanArea[]) =>
      setState((state) => {
        state.japanAreas = newJapanAreas;
      }),
    resetArea: () =>
      setState((state) => {
        state.japanAreas = [];
        state.locale = "";
        state.allLocaleHotels = null;
      }),
    fetchAllLocaleHotels: async (isDraftMode = false, _forceDefaultLocale, targetLocale) => {
      const resolvedLocale = targetLocale || DEFAULT_LOCALE;
      setState((state) => {
        state.loadingAllLocaleHotels = true;
        state.error = null;
      });
      try {
        const hotelsData = await fetchCMS<JapanHotelsQuery>(
          GET_HOTEL_ALL_LOCALE,
          {
            status: isDraftMode
              ? CONTENT_STATUS.DRAFT
              : CONTENT_STATUS.PUBLISHED,
          },
          resolvedLocale,
        );
        setState((state) => {
          state.allLocaleHotels = hotelsData;
          state.loadingAllLocaleHotels = false;
        });
      } catch (err) {
        setState((state) => {
          state.loadingAllLocaleHotels = false;
          state.error =
            err instanceof Error
              ? err.message
              : "Failed to fetch all locale hotels";
        });
      }
    },
  }),
  "japan-area-stores",
  createExpiringIndexedDBStorage(3 * 60 * 60 * 1000), // cache TTL: 3 hours
);

export default useJapanAreasStore;
